import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { AxiosError } from "axios";
import { mutate } from "swr";
import type { ProductVariantDto } from "../../../../api";
import { likesService } from "../../../api_client";
import type { PaginatedResponse } from "../../../pagination/data";

/**
 * Variant-level wishlist ("likes") store. Each entry is a product-variant id.
 * Routes through the generated `LikesApi` so requests share the same auth
 * interceptor as the rest of the app.
 */

export const wishlistApi = {
  async like(variantId: number): Promise<void> {
    await likesService.likeProductVariant(variantId);
  },
  async unlike(variantId: number): Promise<void> {
    await likesService.unlikeProductVariant(variantId);
  },
  async listIds(): Promise<number[]> {
    const res = await likesService.getMyLikedVariantIds();
    return res.data;
  },
};

/**
 * SWR key prefix of the paginated wishlist pages (`useWishlistVariants`).
 * Declared here so the store can invalidate them without importing the hook.
 */
export const WISHLIST_PAGES_KEY_PREFIX = "likes-me-";

/**
 * Bring cached wishlist pages in line with a like/unlike that just succeeded.
 * Without this, a page cached before the change is served as-is on the next
 * visit and `syncLikedStates` re-adds the variant the user just removed.
 * An unliked variant is dropped from every cached page so an open wishlist
 * doesn't flash a loader; a newly liked one clears the cache instead, since
 * the backend orders by like time and only a refetch knows where it lands.
 */
function reconcileWishlistPages(variantId: number, liked: boolean): void {
  void mutate<PaginatedResponse<ProductVariantDto>>(
    (key) => typeof key === "string" && key.startsWith(WISHLIST_PAGES_KEY_PREFIX),
    (page) =>
      liked || !page
        ? undefined
        : { ...page, data: page.data.filter((v) => v.id !== variantId) },
  );
}

interface WishlistState {
  wishlistVariantIds: number[];
  /** Ids with an in-flight like/unlike request — used to debounce rapid clicks. */
  pendingIds: number[];
  isInWishlist: (variantId: number) => boolean;
  toggle: (variantId: number) => Promise<void>;
  add: (variantId: number) => Promise<void>;
  remove: (variantId: number) => Promise<void>;
  loadLikedIds: () => Promise<void>;
  /**
   * Reconcile the store with backend-reported `liked` flags arriving on
   * variant DTOs. Any variant currently in flight is skipped so we don't clobber
   * an optimistic toggle before its request resolves.
   */
  syncLikedStates: (
    entries: ReadonlyArray<{ id: number; liked?: boolean | null }>,
  ) => void;
  clear: () => void;
}

function isMissing(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 404;
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      wishlistVariantIds: [],
      pendingIds: [],

      isInWishlist: (variantId) => get().wishlistVariantIds.includes(variantId),

      add: async (variantId) => {
        const state = get();
        if (
          state.wishlistVariantIds.includes(variantId) ||
          state.pendingIds.includes(variantId)
        ) {
          return;
        }
        set({
          wishlistVariantIds: [...state.wishlistVariantIds, variantId],
          pendingIds: [...state.pendingIds, variantId],
        });
        try {
          await wishlistApi.like(variantId);
          reconcileWishlistPages(variantId, true);
        } catch (error) {
          set({
            wishlistVariantIds: get().wishlistVariantIds.filter(
              (id) => id !== variantId,
            ),
          });
          throw error;
        } finally {
          set({
            pendingIds: get().pendingIds.filter((id) => id !== variantId),
          });
        }
      },

      remove: async (variantId) => {
        const state = get();
        if (
          !state.wishlistVariantIds.includes(variantId) ||
          state.pendingIds.includes(variantId)
        ) {
          return;
        }
        const previous = state.wishlistVariantIds;
        set({
          wishlistVariantIds: previous.filter((id) => id !== variantId),
          pendingIds: [...state.pendingIds, variantId],
        });
        try {
          await wishlistApi.unlike(variantId);
          reconcileWishlistPages(variantId, false);
        } catch (error) {
          if (isMissing(error)) {
            reconcileWishlistPages(variantId, false);
            return;
          }
          set({ wishlistVariantIds: previous });
          throw error;
        } finally {
          set({
            pendingIds: get().pendingIds.filter((id) => id !== variantId),
          });
        }
      },

      toggle: async (variantId) => {
        const { isInWishlist, add, remove } = get();
        if (isInWishlist(variantId)) await remove(variantId);
        else await add(variantId);
      },

      syncLikedStates: (entries) => {
        const state = get();
        const pending = new Set(state.pendingIds);
        const next = new Set(state.wishlistVariantIds);
        let changed = false;
        for (const entry of entries) {
          if (typeof entry.liked !== "boolean") continue;
          if (pending.has(entry.id)) continue;
          if (entry.liked) {
            if (!next.has(entry.id)) {
              next.add(entry.id);
              changed = true;
            }
          } else if (next.has(entry.id)) {
            next.delete(entry.id);
            changed = true;
          }
        }
        if (changed) set({ wishlistVariantIds: Array.from(next) });
      },

      loadLikedIds: async () => {
        try {
          const ids = await wishlistApi.listIds();
          set({ wishlistVariantIds: ids });
        } catch {
          set({ wishlistVariantIds: [] });
        }
      },

      clear: () => set({ wishlistVariantIds: [], pendingIds: [] }),
    }),
    {
      name: "wishlist-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        wishlistVariantIds: state.wishlistVariantIds,
      }),
    },
  ),
);
